// packages import
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  CircularProgress,
  DialogActions,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material'
import { FC, useContext } from 'react'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
// component import
import { Alert } from '../common/Alert'
import { CustomController } from '../common/CustomController'
import { CustomModal } from '../common/CustomModal'
// others
import { useRegisterUserMutation, UserRole } from '../../../generated'
import {
  CANCEL,
  CONFLICT_EXCEPTION,
  CREATE_USER_SUCCESS,
  INVITE_NEW_USER,
  SOMETHING_WENT_WRONG,
  USER_ALREADY_EXIST
} from '../../constants'
import { AppContext } from '../../context/AppContext'
import { ActionType } from '../../context/AppContextReducer'
import { ErrorException, InviteNewUserProps, RegisterUserInputProps } from '../../interfaceTypes/index'
import { anchorOrigin, transformOrigin } from '../../theme/styleConstants'
import { titleize } from '../../utils'
import { registerUserValidationSchema } from '../../validationSchema'
import SelectDelegates from './SelectDelegates'
import SelectDepartments from './SelectDepartments'
import OfficeAutoComplete from '../offices/OfficeAutocomplete'

export const InviteNewUser: FC<InviteNewUserProps> = ({ refetch, state }): JSX.Element => {
  const { isOpen, dispatch, rolesData } = useContext(AppContext)

  const methods = useForm<RegisterUserInputProps>({
    mode: 'all',
    resolver: yupResolver(registerUserValidationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      repeatPassword: '',
      roleType: UserRole.Admin,
      initials: '',
      hourlyRate: 0,
      delegateIds: [],
      departmentIds: []
    }
  })

  const { handleSubmit, reset, control, watch } = methods

  const [registerUserMutation, { loading }] = useRegisterUserMutation({
    onError({ message, graphQLErrors }) {
      if (graphQLErrors) {
        graphQLErrors.map(
          // eslint-disable-next-line
          ({ extensions }) => {
            if (extensions) {
              const { exception } = extensions
              if (exception) {
                const { response } = exception as ErrorException
                const { response: errorResponse } = response || {}
                if (errorResponse) {
                  const { error } = errorResponse

                  if (error === 'User with this initials already exists') {
                    Alert.error('User with this initials already exists')
                  } else {
                    if (message === CONFLICT_EXCEPTION) {
                      return Alert.error(USER_ALREADY_EXIST)
                    }
                    Alert.error(SOMETHING_WENT_WRONG)
                  }
                }
              }
            }
          }
        )
      }
    },

    onCompleted(data) {
      if (data) {
        const {
          registerUser: { response, user }
        } = data

        if (response) {
          const { status } = response

          if (status === 200 && user) {
            Alert.success(CREATE_USER_SUCCESS)
            dispatch({ type: ActionType.SET_IS_OPEN, isOpen: !isOpen })
            reset()
            handleClose()
            refetch && refetch()
            dispatch({ type: ActionType.SET_SEARCH_VALUE, searchValue: '' })
          }
        }
      }
    }
  })

  const handleClose = () => dispatch({ type: ActionType.SET_IS_OPEN, isOpen: !isOpen })

  const onSubmit: SubmitHandler<RegisterUserInputProps> = async (data: RegisterUserInputProps) => {
    const { delegateIds, office, departmentIds, firstName, lastName, email, roleType, password, initials, hourlyRate } =
      data
    await registerUserMutation({
      variables: {
        registerUserInput: {
          firstName: titleize(firstName?.trim() || ''),
          lastName: titleize(lastName?.trim() || ''),
          email,
          roleType,
          password,
          initials,
          hourlyRate,
          delegateIds,
          departmentIds,
          officeId: office?.value
        }
      }
    })
  }

  return (
    <CustomModal title={INVITE_NEW_USER} isOpen={isOpen} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <CustomController controllerName="email" controllerLabel="Email" fieldType="email" />

          <Grid container spacing={2} rowSpacing={2} mt={0.5}>
            <Grid item md={6} sm={12} xs={12}>
              <CustomController controllerName="firstName" controllerLabel="First Name" fieldType="text" />
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <CustomController controllerName="lastName" controllerLabel="Last Name" fieldType="text" />
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <FormControl variant="outlined" sx={{ marginTop: 0.5, marginBottom: 3 }} fullWidth>
                <CustomController controllerName="initials" controllerLabel="Initials" fieldType="text" upperCase />
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2} rowSpacing={0.5}>
            <Grid item md={6} sm={12} xs={12}>
              <CustomController controllerName="password" controllerLabel="Password" fieldType="password" isPassword />
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <CustomController
                controllerName="repeatPassword"
                controllerLabel="Confirm Password"
                fieldType="password"
                isPassword
              />
            </Grid>
          </Grid>

          <Divider style={{ margin: '25px 0' }} />

          <Grid container spacing={2} rowSpacing={0.5}>
            <Grid item md={6} sm={12} xs={12}>
              <FormControl variant="outlined" sx={{ marginTop: 1.5, marginBottom: 1 }} fullWidth>
                <InputLabel id="role-select">Role</InputLabel>

                <Controller
                  name="roleType"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="role-select"
                      label="Role"
                      fullWidth
                      MenuProps={{ anchorOrigin: anchorOrigin, transformOrigin: transformOrigin }}
                    >
                      {rolesData?.map((item, index) => {
                        const { label, value } = item

                        return (
                          <MenuItem value={value} key={`${label}-${index}`}>
                            {label}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item md={6} sm={12} xs={12}>
              <FormControl variant="outlined" sx={{ marginTop: 0.5 }} fullWidth>
                <CustomController controllerName="hourlyRate" controllerLabel="Hourly Rate" fieldType="number" />
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2} rowSpacing={0.5}>
            <Grid item md={12} sm={12} xs={12}>
              <SelectDepartments />
            </Grid>

            <Grid item md={12} sm={12} xs={12}>
              <OfficeAutoComplete label="Profit Center" name="office" autoSelectOnTab />
            </Grid>
          </Grid>

          <Grid container spacing={2} rowSpacing={0.5}>
            <Grid item md={12} sm={12} xs={12}>
              {(watch('roleType') === UserRole.Attorney || watch('roleType') === UserRole.Investigator) && (
                <SelectDelegates />
              )}
            </Grid>
          </Grid>

          <DialogActions sx={{ padding: '30px 0 0' }}>
            <Button onClick={handleClose} variant="outlined" aria-label={CANCEL}>
              {CANCEL}
            </Button>

            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={loading}
              aria-label="send invite"
              endIcon={loading && <CircularProgress size={20} color="inherit" />}
            >
              SEND INVITE
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </CustomModal>
  )
}
